"use client"

import * as React from "react"
import { VariantProps, cva } from "class-variance-authority"
import { cn } from '../../cn'
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
} from "./sheet"
import { X } from "lucide-react"

// Import modular sidebar components
import { SidebarProvider } from "../../../layout/src/components/layout/SidebarProvider"
import { SidebarContent } from "../../../layout/src/components/layout/SidebarContent"
import {
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
} from "../../../layout/src/components/layout/SidebarLayout"
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "../../../layout/src/components/layout/SidebarMenu"
import {
  SidebarInset,
  SidebarInput,
  SidebarSeparator,
  SidebarTrigger,
} from "../../../layout/src/components/layout/SidebarControls"

// Re-export all sidebar components
export {
  SidebarProvider,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarInput,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
}

// Main Sidebar component
const sidebarVariants = cva(
  "group/sidebar-wrapper flex h-full flex-col bg-sidebar text-sidebar-foreground",
  {
    variants: {
      variant: {
        default: "border-r border-sidebar-border",
        floating: "border border-sidebar-border shadow-lg",
        inset: "border-r border-sidebar-border",
      },
      side: {
        left: "border-r",
        right: "border-l",
      },
    },
    defaultVariants: {
      variant: "default",
      side: "left",
    },
  }
)

export interface SidebarProps
  extends React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof sidebarVariants> {
  side?: "left" | "right"
  variant?: "default" | "floating" | "inset"
  collapsible?: "offcanvas" | "icon" | "none"
    defaultOpen?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
  }

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      side = "left",
      variant = "default",
      collapsible = "offcanvas",
      defaultOpen = true,
      open: openProp,
      onOpenChange,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [openMobile, setOpenMobile] = React.useState(false)

    // Use sheet (overlay) for offcanvas to prevent content shift and allow dimming on all breakpoints
    const shouldUseSheet = collapsible === "offcanvas"
    const open = openProp ?? (shouldUseSheet ? openMobile : defaultOpen)
    const setOpen = onOpenChange ?? (shouldUseSheet ? setOpenMobile : undefined)

    if (collapsible === "none") {
      return (
        <div
          ref={ref}
          className={cn(sidebarVariants({ variant, side }), className)}
          {...props}
        >
          {children}
        </div>
      )
    }

    if (shouldUseSheet) {
      return (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            data-sidebar="sidebar"
            side={side}
            aria-label="Main navigation"
            className="w-64 max-w-sidebar px-3 py-0 bg-sidebar text-sidebar-foreground overflow-hidden [&>button]:hidden"
          >
            {/* BUG-2026-06-16-WGC1P: Radix requires a registered Title on every open
                Sheet/Dialog; the `aria-label` above does NOT satisfy it. This is the
                mobile-nav surface reachable on every route (incl. /atc/bridge/agents/chat),
                so give it a meaningful sr-only Title (beats the generic fallback). */}
            <SheetTitle className="sr-only">Main navigation</SheetTitle>
            <nav
              role="navigation"
              aria-label="Main navigation"
              className="flex h-full w-full flex-col overflow-y-auto overflow-x-hidden layout-sidebar-rail [scrollbar-width:thin] overscroll-contain"
            >
              <div className="sticky top-0 z-10 flex items-center gap-3 py-3 bg-sidebar">
                <SheetClose
                  className="h-10 w-10 p-2 rounded-full flex items-center justify-center hover:bg-muted"
                  aria-label="Close sidebar"
                  title="Close sidebar"
                >
                  <X className="h-6 w-6" strokeWidth={2} />
                </SheetClose>
              </div>
              {children}
            </nav>
          </SheetContent>
        </Sheet>
      )
    }

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          sidebarVariants({ variant, side }),
          "overflow-y-auto overflow-x-hidden h-full flex-shrink-0 flex-grow-0",
          !open && collapsible === "icon" && "hidden",
          open ? "w-64 min-w-64 max-w-64" : "w-0 min-w-0 max-w-0",
          className
        )}
        data-state={open ? "expanded" : "collapsed"}
        {...props}
      >
        {children}
      </nav>
    )
  }
)
Sidebar.displayName = "Sidebar"

export { Sidebar }